// TechnicianReports.jsx content
import { useEffect, useState } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../services/firebase";
import { useUserData } from "../hooks/useUserData";
import { useNavigate } from "react-router-dom";
import { FileText, Calendar, CheckCircle, Clock, AlertTriangle } from "lucide-react";

export default function TechnicianReports() {
  const navigate = useNavigate();
  const [user, userLoading] = useUserData();
  const [completedTasks, setCompletedTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (userLoading) return;

    if (!user) {
      navigate("/login");
      return;
    }

    if (user.role !== "technician") {
      navigate(user.role === "admin" ? "/admin/dashboard" : "/dashboard");
      return;
    }

    // Load completed maintenance tasks for reporting
    const tasksQuery = query(
      collection(db, "maintenanceTasks"),
      where("assignedTechnician", "==", user.uid),
      where("status", "==", "completed")
    );

    const unsubTasks = onSnapshot(
      tasksQuery,
      (snap) => {
        const tasksData = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setCompletedTasks(tasksData);
        setLoading(false);
      },
      (error) => {
        console.error("Error loading completed tasks:", error);
        setLoading(false);
      }
    );

    return () => unsubTasks();
  }, [user, userLoading, navigate]);

  const totalCompleted = completedTasks.length;
  const thisMonthCompleted = completedTasks.filter(task => {
    const completedDate = task.completedAt?.toDate();
    const now = new Date();
    return completedDate &&
           completedDate.getMonth() === now.getMonth() &&
           completedDate.getFullYear() === now.getFullYear();
  }).length;

  const totalLaborHours = completedTasks.reduce((sum, task) => sum + (task.laborHours || 0), 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50 flex flex-col justify-center items-center animate-fade-in">
        <div className="skeleton-avatar mb-4"></div>
        <div className="skeleton-title w-48 mb-2"></div>
        <div className="skeleton-text w-32"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 animate-fade-in">
      <div className="w-full px-4 pt-20 space-y-6 lg:px-8 xl:px-12">
        {/* Header */}
        <div className="text-center">
          <div className="w-16 h-16 bg-primary-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <FileText className="w-8 h-8 text-primary-600" />
          </div>
          <h1 className="text-2xl font-bold text-neutral-900 mb-2">
            Maintenance Reports
          </h1>
          <p className="text-neutral-600">
            View your maintenance history and statistics
          </p>
        </div>

        {/* Summary Cards */}
        <section>
          <h2 className="text-xl font-semibold text-neutral-900 mb-4">Summary</h2>
          <div className="grid grid-cols-2 gap-4">
            <StatCard
              icon={<CheckCircle className="w-6 h-6" />}
              title="Total Completed"
              value={totalCompleted}
              color="success"
            />
            <StatCard
              icon={<Calendar className="w-6 h-6" />}
              title="This Month"
              value={thisMonthCompleted}
              color="info"
            />
            <StatCard
              icon={<Clock className="w-6 h-6" />}
              title="Labor Hours"
              value={`${totalLaborHours}h`}
              color="warning"
            />
            <StatCard
              icon={<AlertTriangle className="w-6 h-6" />}
              title="Avg. per Task"
              value={totalCompleted > 0 ? `${(totalLaborHours / totalCompleted).toFixed(1)}h` : "0h"}
              color="neutral"
            />
          </div>
        </section>

        {/* Recent Completed Tasks */}
        <section>
          <h2 className="text-xl font-semibold text-neutral-900 mb-4">Recent Completed Tasks</h2>
          <div className="space-y-4">
            {completedTasks.length === 0 ? (
              <div className="card text-center py-12">
                <CheckCircle className="w-16 h-16 text-neutral-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-neutral-900 mb-2">No Completed Tasks</h3>
                <p className="text-neutral-600">You haven't completed any maintenance tasks yet.</p>
              </div>
            ) : (
              completedTasks.slice(0, 10).map((task) => (
                <div key={task.id} className="card-interactive p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h3 className="font-semibold text-neutral-900 mb-1">{task.assetName || "Unnamed Asset"}</h3>
                      <p className="text-sm text-neutral-600 mb-2">{task.description}</p>
                      <div className="flex items-center gap-4 text-xs text-neutral-500">
                        <span>📅 {task.completedAt ? new Date(task.completedAt.toDate()).toLocaleDateString() : "Unknown"}</span>
                        {task.laborHours && <span>⏱️ {task.laborHours}h</span>}
                      </div>
                    </div>
                    <CheckCircle className="w-5 h-5 text-success-600 flex-shrink-0 ml-3" />
                  </div>
                </div>
              ))
            )}
          </div>
        </section>
      </div>

    </div>
  );
}

// Helper Component
function StatCard({ icon, title, value, color }) {
  const getColorClasses = (color) => {
    switch (color) {
      case 'primary': return 'bg-primary-500 text-white';
      case 'success': return 'bg-success-500 text-white';
      case 'warning': return 'bg-warning-500 text-white';
      case 'error': return 'bg-error-500 text-white';
      case 'info': return 'bg-info-500 text-white';
      case 'neutral': return 'bg-neutral-500 text-white';
      default: return 'bg-neutral-500 text-white';
    }
  };

  return (
    <div className="card-interactive p-6 text-center animate-fade-in">
      <div className={`w-12 h-12 rounded-2xl ${getColorClasses(color)} flex items-center justify-center mx-auto mb-4 shadow-lg`}>
        {icon}
      </div>
      <div className="text-2xl font-bold text-neutral-900 mb-1">{value}</div>
      <div className="text-sm font-medium text-neutral-700">{title}</div>
    </div>
  );
}
